import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FilePenLine, BadgeDollarSign, Download, Upload, FileText, ReceiptText, Sun, RotateCcw } from "lucide-react";
import api, { apiError, downloadBlob } from "@/lib/api";
import Modal from "@/components/Modal";
import Field from "@/components/Field";
import StatusBadge from "@/components/StatusBadge";
import DocPreview from "@/components/DocPreview";
import { fmtAUD, fmtAUDex, fmtDate, exGst } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const MAX_VAR_DOCS = 5;

const STATUSES = ["Draft", "Submitted", "Approved", "Rejected", "Cancelled"];
export const VARIATION_TONE = { Draft: "amber", Submitted: "green", Approved: "green", Rejected: "red", Cancelled: "grey" };
const TONE = VARIATION_TONE;

export function VariationForm({ project, variation, docs, refreshDocs, onClose, onSaved, prefill, source, titleOverride }) {
  // Solar-linked variations are edited Excl. GST (stored GST-inc ÷ 1.1); an unchanged value re-saves the stored amount exactly (no drift).
  const exEdit = !!variation && variation.source_type === "solar_system";
  const storedEx = exEdit ? exGst(variation.amount) : null;
  const [form, setForm] = useState({
    date_raised: variation?.date_raised || new Date().toISOString().slice(0, 10),
    description: variation?.description || prefill?.description || "",
    amount: variation ? (exEdit ? storedEx : variation.amount) : "",
    status: variation?.status || "Draft",
    date_approved: variation?.date_approved || "",
    notes: variation?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const locked = variation?.claimed;
  const isNew = !variation;
  const exGstEntered = parseFloat(form.amount || 0) || 0;
  const gstInclusive = Math.round(exGstEntered * 1.1 * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let amount;
      if (isNew) amount = gstInclusive;
      else if (exEdit) amount = Math.abs(exGstEntered - storedEx) < 0.0000001 ? variation.amount : gstInclusive;
      else amount = parseFloat(form.amount || 0);
      const payload = { ...form, amount, date_approved: form.date_approved || null, ...(isNew && source ? source : {}) };
      if (variation) {
        await api.patch(`/variations/${variation.id}`, payload);
      } else {
        const { data } = await api.post(`/projects/${project.id}/variations`, payload);
        for (const file of pendingFiles) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("category", "Variation Support");
          fd.append("linked_to", `variation:${data.id}`);
          await api.post(`/projects/${project.id}/documents`, fd);
        }
        if (pendingFiles.length) refreshDocs?.();
      }
      toast.success(variation ? `${variation.code} updated` : "Variation raised");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={titleOverride || (variation ? `Edit ${variation.code}` : "Raise New Variation")} onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="variation-form">
        <Field label="Date Raised"><input data-testid="var-date-raised" type="date" className="bdx-input" value={form.date_raised} onChange={set("date_raised")} /></Field>
        <Field label={isNew || exEdit ? "Variation Amount (Excluding GST)" : "Variation Amount (GST Inclusive)"}>
          <input data-testid="var-amount" type="number" step="0.01" className="bdx-input font-mono-data" required disabled={locked} value={form.amount} onChange={set("amount")} />
        </Field>
        {(isNew || exEdit) && (
          <div className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2.5 sm:col-span-2" data-testid="var-gst-inclusive-strip">
            <div className="flex items-center justify-between">
              <span className="bdx-label text-gray-500">Variation Value (GST Inclusive)</span>
              <span className="font-mono-data text-sm font-semibold text-gray-600" data-testid="var-gst-inclusive-value">{fmtAUD(gstInclusive)}</span>
            </div>
            <p className="mt-1 text-[10px] text-gray-400">Display only — calculated automatically as Variation Amount (ex GST) × 1.1</p>
          </div>
        )}
        <Field label="Description" className="sm:col-span-2">
          <textarea data-testid="var-description" className="bdx-input" rows={2} required value={form.description} onChange={set("description")} />
        </Field>
        <Field label="Status">
          <select data-testid="var-status" className="bdx-input" disabled={locked} value={form.status} onChange={set("status")}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Date Approved"><input data-testid="var-date-approved" type="date" className="bdx-input" value={form.date_approved} onChange={set("date_approved")} /></Field>
        <Field label="Notes" className="sm:col-span-2"><textarea data-testid="var-notes" className="bdx-input" rows={2} value={form.notes} onChange={set("notes")} /></Field>
        {variation ? (
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500 sm:col-span-2" data-testid="var-edit-docs-hint">
            Supporting documents are managed directly from the variation row in the register (paperclip to add, eye to view).
          </p>
        ) : (
          <div className="sm:col-span-2 rounded-md border border-gray-200 bg-[#faf9f8] px-3 py-2.5 shadow-sm" data-testid="var-new-docs">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#d9cde5] bg-[#f3eff7] text-[#604184]"><FileText size={14} strokeWidth={1.8} /></span>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-700">Supporting Documents</div>
                  {pendingFiles.length === 0
                    ? <div className="text-[11px] text-gray-400">No documents attached — uploaded with the variation</div>
                    : <div className="text-[11px] text-gray-500">{pendingFiles.length} document{pendingFiles.length > 1 ? "s" : ""} ready to upload</div>}
                </div>
              </div>
              <div className="shrink-0">
                <input data-testid="var-new-docs-input" type="file" multiple className="hidden" id="var-new-docs-file"
                  onChange={(e) => { setPendingFiles((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, MAX_VAR_DOCS)); e.target.value = ""; }} />
                <label htmlFor="var-new-docs-file" data-testid="var-new-docs-upload" className="bdx-icon-btn cursor-pointer" title="Attach supporting documents">
                  <Upload size={14} strokeWidth={1.5} />
                </label>
              </div>
            </div>
            {pendingFiles.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 border-t border-gray-200" data-testid="var-new-docs-list">
                {pendingFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 py-1.5">
                    <span className="truncate font-mono-data text-[11px] font-medium text-gray-800">{f.name}</span>
                    <button type="button" data-testid={`var-new-doc-remove-${i}`} className="bdx-icon-btn-danger h-7 w-7 shrink-0" title="Remove"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, x) => x !== i))}><Trash2 size={13} strokeWidth={1.6} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {locked && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700 sm:col-span-2">This variation has been claimed — its amount and status are locked.</p>}
        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3 sm:col-span-2">
          <button type="button" data-testid="var-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="var-submit" disabled={busy} className="bdx-btn-primary">{busy ? "Saving…" : "Save Variation"}</button>
        </div>
      </form>
    </Modal>
  );
}

function AttachmentsCell({ project, linkedTo, matchKeys, category, modalTitle, toastLabel, rowPrefix, docPrefix, rowKey, docs, refreshDocs, readOnly }) {
  const keys = matchKeys || [linkedTo];
  const items = (docs || []).filter((d) => keys.includes(d.linked_to));
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [listOpen, setListOpen] = useState(false);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (items.length + list.length > MAX_VAR_DOCS) {
      toast.error(`Up to ${MAX_VAR_DOCS} attachments are supported (${items.length} already attached)`);
      return;
    }
    setBusy(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        fd.append("linked_to", linkedTo);
        await api.post(`/projects/${project.id}/documents`, fd);
      }
      toast.success(`${list.length} document${list.length > 1 ? "s" : ""} attached to ${toastLabel}`);
      refreshDocs?.();
    } catch (e) { toast.error(apiError(e)); } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Delete this attachment?")) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Attachment deleted"); refreshDocs?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const view = () => (items.length === 1 ? setPreview(items[0]) : setListOpen(true));

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid={`${rowPrefix}-${rowKey}`}>
      <span className="inline-flex w-[4.5rem] shrink-0 items-center">
        {items.length > 0 ? (
          <button data-testid={`${rowPrefix}-view-${rowKey}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase text-blue-700 transition-colors hover:bg-blue-100" style={{ letterSpacing: "0.05em" }}
            title={items.length === 1 ? `View ${items[0].filename}` : `View ${items.length} attachments`} onClick={view}>
            View{items.length > 1 && <span className="font-mono-data normal-case">· {items.length}</span>}
          </button>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </span>
      {!readOnly && (
        <>
          <input ref={inputRef} data-testid={`${rowPrefix}-input-${rowKey}`} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button data-testid={`${rowPrefix}-add-${rowKey}`} className="bdx-icon-btn h-8 w-8 shrink-0 disabled:opacity-40" disabled={busy || items.length >= MAX_VAR_DOCS}
            title={items.length >= MAX_VAR_DOCS ? `Maximum ${MAX_VAR_DOCS} attachments` : "Add supporting document"} onClick={() => inputRef.current?.click()}>
            <Upload size={14} strokeWidth={1.6} />
          </button>
        </>
      )}
      {listOpen && (
        <Modal title={modalTitle} onClose={() => setListOpen(false)}>
          <ul className="divide-y divide-gray-100" data-testid={`${rowPrefix}-list-${rowKey}`}>
            {items.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <button className="block max-w-full truncate text-left font-mono-data text-xs font-medium text-gray-800 hover:text-[#604184] hover:underline" onClick={() => setPreview(d)} data-testid={`${docPrefix}-open-${d.id}`}>{d.filename}</button>
                  <div className="text-[10px] text-gray-500">{fmtDate(d.uploaded_at)} · {d.uploaded_by}</div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button className="inline-flex items-center rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase text-blue-700 transition-colors hover:bg-blue-100" style={{ letterSpacing: "0.05em" }} title="View" onClick={() => setPreview(d)} data-testid={`${docPrefix}-view-${d.id}`}>View</button>
                  <button className="bdx-icon-btn h-7 w-7" title="Download" onClick={() => downloadBlob(`/documents/${d.id}/download`, d.filename)} data-testid={`${docPrefix}-download-${d.id}`}><Download size={13} strokeWidth={1.6} /></button>
                  {!readOnly && <button className="bdx-icon-btn-danger h-7 w-7" title="Delete" onClick={() => remove(d)} data-testid={`${docPrefix}-delete-${d.id}`}><Trash2 size={13} strokeWidth={1.6} /></button>}
                </div>
              </li>
            ))}
          </ul>
        </Modal>
      )}
      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function VariationFilesCell({ project, variation, docs, refreshDocs, readOnly }) {
  return (
    <AttachmentsCell project={project} linkedTo={`variation:${variation.id}`} category="Variation Support"
      modalTitle={`${variation.code} — Supporting Documents`} toastLabel={variation.code}
      rowPrefix="var-files" docPrefix="var-file" rowKey={variation.code}
      docs={docs} refreshDocs={refreshDocs} readOnly={readOnly} />
  );
}

const EXT_STATUS_CLS = {
  red: "border-red-300 bg-red-50 text-red-700",
  orange: "border-orange-200 bg-orange-50 text-orange-600",
  green: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

// stored values unchanged; legacy "reference" presents as Pending (v1.0.17)
const MANUAL_STATUSES = [["reference", "Pending"], ["unpaid", "Unpaid"], ["paid", "Paid"]];

function ExternalCostDialog({ project, record, onClose, onSaved }) {
  const [form, setForm] = useState({
    description: record?.description || "",
    amount: record?.amount ?? "",
    status: record?.status || "reference",
    due_date: record?.due_date || "",
    paid_date: record?.paid_date || "",
    notes: record?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), due_date: form.due_date || null, paid_date: form.paid_date || null };
      if (record) {
        await api.patch(`/projects/${project.id}/external-costs/manual/${record.id}`, payload);
      } else {
        const { data } = await api.post(`/projects/${project.id}/external-costs/manual`, payload);
        for (const file of pendingFiles) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("category", "External Cost");
          fd.append("linked_to", `extcost:manual:${data.id}`);
          await api.post(`/projects/${project.id}/documents`, fd);
        }
      }
      toast.success(record ? "VQR Cost updated" : "VQR Cost raised — reference only, outside contract financials");
      onSaved(pendingFiles.length > 0);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={record ? "Edit VQR Cost" : "Raise VQR Cost"} onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="external-cost-dialog">
        <p className="rounded-sm border border-orange-200 bg-orange-50 px-3 py-2 text-[11px] leading-relaxed text-orange-700 sm:col-span-2">
          Miscellaneous VQR Cost — reference / project-cost visibility only. It is never numbered as a Variation and never affects the Base Contract, Approved Variations, Contract Total, Claims or completion.
        </p>
        <Field label="Description" className="sm:col-span-2">
          <input data-testid="ext-manual-description" className="bdx-input" required value={form.description} onChange={set("description")} placeholder="e.g. Council permit fee" />
        </Field>
        <Field label="Amount ($ Excl. GST)">
          <input data-testid="ext-manual-amount" type="number" step="0.01" min="0" className="bdx-input font-mono-data" required value={form.amount} onChange={set("amount")} />
        </Field>
        <Field label="Status">
          <select data-testid="ext-manual-status" className="bdx-input" value={form.status} onChange={set("status")}>
            {MANUAL_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Due Date (optional)"><input data-testid="ext-manual-due-date" type="date" className="bdx-input" value={form.due_date} onChange={set("due_date")} /></Field>
        <Field label="Paid Date (optional)"><input data-testid="ext-manual-paid-date" type="date" className="bdx-input" value={form.paid_date} onChange={set("paid_date")} /></Field>
        <Field label="Notes (optional)" className="sm:col-span-2"><textarea data-testid="ext-manual-notes" className="bdx-input" rows={2} value={form.notes} onChange={set("notes")} /></Field>
        {record ? (
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500 sm:col-span-2">Supporting / reference documents are managed from the row in the VQR Costs Register.</p>
        ) : (
          <div className="sm:col-span-2 rounded-md border border-gray-200 bg-[#faf9f8] px-3 py-2.5 shadow-sm" data-testid="ext-manual-docs">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-orange-200 bg-orange-50 text-orange-600"><FileText size={14} strokeWidth={1.8} /></span>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-700">Supporting / Reference Documents</div>
                  <div className="text-[11px] text-gray-400">{pendingFiles.length === 0 ? "No documents attached — uploaded with the VQR Cost" : `${pendingFiles.length} document${pendingFiles.length > 1 ? "s" : ""} ready to upload`}</div>
                </div>
              </div>
              <div className="shrink-0">
                <input data-testid="ext-manual-docs-input" type="file" multiple className="hidden" id="ext-manual-docs-file"
                  onChange={(e) => { setPendingFiles((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, MAX_VAR_DOCS)); e.target.value = ""; }} />
                <label htmlFor="ext-manual-docs-file" data-testid="ext-manual-docs-upload" className="bdx-icon-btn cursor-pointer" title="Attach documents"><Upload size={14} strokeWidth={1.5} /></label>
              </div>
            </div>
            {pendingFiles.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 border-t border-gray-200">
                {pendingFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 py-1.5">
                    <span className="truncate font-mono-data text-[11px] font-medium text-gray-800">{f.name}</span>
                    <button type="button" className="bdx-icon-btn-danger h-7 w-7 shrink-0" title="Remove" onClick={() => setPendingFiles((prev) => prev.filter((_, x) => x !== i))}><Trash2 size={13} strokeWidth={1.6} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3 sm:col-span-2">
          <button type="button" data-testid="ext-manual-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="ext-manual-submit" disabled={busy} className="bdx-btn-primary border-orange-600 bg-orange-600 hover:bg-orange-700">{busy ? "Saving…" : record ? "Save VQR Cost" : "Raise VQR Cost"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ExternalCostsSection({ project, docs, refresh, refreshDocs, readOnly }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, record=edit

  const load = useCallback(() => api.get(`/projects/${project.id}/external-costs`).then((r) => setRows(r.data)), [project.id]);
  useEffect(() => { load(); }, [load, project]);

  const reset = async (row) => {
    const msg = row.origin === "manual"
      ? `Delete VQR Cost "${row.name}"?\n\nThis removes the manual VQR Cost and its reference attachments. Roadmap data and contract financials are not affected.`
      : row.source_key === "electrical_authority_quote"
        ? `Reset "${row.name}"?\n\nThis removes the Application Quote (date, amount, quote documents) from the Distributor Application. The Application Cost is NOT affected.`
        : row.mode === "dwelling_item"
          ? `Remove "${row.name}"?\n\nThis removes the additional VQR Cost item from the Distributor Submission and its reference attachments.`
          : `Reset "${row.name}"?\n\nThis returns the originating Roadmap VQR Cost to its Not Recorded state and removes this reference row (including its reference attachments). This is not a Variation deletion.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/projects/${project.id}/external-costs/${row.source_key}`);
      toast.success(row.origin === "manual" ? `${row.name} deleted` : `${row.name} reset`);
      load(); refresh?.(); refreshDocs?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!rows) return null;
  if (rows.length === 0 && readOnly) return null;

  return (
    <div className="mt-12 border-t-2 border-dashed border-orange-200 pt-8" data-testid="external-costs-section">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-orange-200 bg-orange-50 text-orange-600"><ReceiptText size={16} strokeWidth={1.9} /></div>
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-950">VQR Costs</h3>
            <p className="mt-1 text-[11px] text-gray-500">Reference costs (Excl. GST) linked live from the Electrical / NBN Roadmaps or raised manually — never numbered as variations and never included in the Approved Variations total, Contract Sum, claims or completion.</p>
          </div>
        </div>
        {!readOnly && (
          <button data-testid="raise-external-cost-btn" className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-orange-500 bg-orange-50 px-3.5 py-2 text-xs font-bold uppercase text-orange-800 shadow-sm transition-colors hover:bg-orange-100" style={{ letterSpacing: "0.06em" }} onClick={() => setEditing(null)}>
            <Plus size={14} strokeWidth={2.2} /> Raise VQR Cost
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-orange-200 bg-orange-50/30 px-4 py-8 text-center text-xs text-gray-400" data-testid="external-costs-empty">No VQR Costs recorded — roadmap VQR Costs appear here automatically; miscellaneous costs can be raised above.</div>
      ) : (
      <div className="overflow-x-auto rounded-lg border border-orange-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <table className="bdx-table table-fixed" style={{ minWidth: "960px" }} data-testid="external-costs-table">
          <colgroup>
            <col style={{ width: "150px" }} />
            <col />
            <col style={{ width: "170px" }} />
            <col style={{ width: "230px" }} />
            <col style={{ width: "118px" }} />
            {!readOnly && <col style={{ width: "100px" }} />}
          </colgroup>
          <thead><tr><th>Type</th><th>Source / Description</th><th className="whitespace-nowrap text-right">Amount (Excl. GST)</th><th>Status</th><th>Files</th>{!readOnly && <th>Actions</th>}</tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.source_key} data-testid={`ext-cost-row-${row.source_key}`} className="bg-orange-50/40">
                <td>
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-orange-300 bg-orange-100 px-2 py-1 text-[10px] font-bold uppercase text-orange-700" style={{ letterSpacing: "0.06em" }}><ReceiptText size={11} strokeWidth={1.9} /> VQR Cost</span>
                  {row.origin === "manual" && <div className="mt-1 text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.06em" }} data-testid={`ext-cost-origin-${row.source_key}`}>Manual</div>}
                </td>
                <td>
                  <div className="truncate text-sm font-semibold text-gray-900" title={row.name}>{row.name}</div>
                  {row.secondary && <div className="truncate text-[10px] text-gray-500" title={row.secondary}>{row.secondary}</div>}
                </td>
                <td className="text-right font-mono-data font-semibold text-gray-950" data-testid={`ext-cost-amount-${row.source_key}`}>{row.amount != null ? fmtAUD(row.amount) : "—"}</td>
                <td><span data-testid={`ext-cost-status-${row.source_key}`} className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase ${EXT_STATUS_CLS[row.status_tone] || EXT_STATUS_CLS.orange}`} style={{ letterSpacing: "0.05em" }}>{row.status_label}</span></td>
                <td>
                  <AttachmentsCell project={project} linkedTo={row.linked_to} matchKeys={row.linked_alias || [row.linked_to]} category="External Cost"
                    modalTitle={`${row.name} — Reference Documents`} toastLabel={row.name}
                    rowPrefix="ext-files" docPrefix="ext-file" rowKey={row.source_key}
                    docs={docs} refreshDocs={refreshDocs} readOnly={readOnly} />
                </td>
                {!readOnly && (
                  <td>
                    <div className="flex items-center gap-1">
                      {row.origin === "manual" && (
                        <button data-testid={`ext-cost-edit-${row.source_key}`} className="bdx-icon-btn" title="Edit VQR Cost" onClick={() => setEditing(row.record)}><Pencil size={14} strokeWidth={1.7} /></button>
                      )}
                      <button data-testid={`ext-cost-reset-${row.source_key}`} className="bdx-icon-btn-danger" title={row.origin === "manual" ? "Delete VQR Cost" : "Reset — returns the Roadmap VQR Cost to Not Recorded"} onClick={() => reset(row)}>
                        {row.origin === "manual" ? <Trash2 size={14} strokeWidth={1.7} /> : <RotateCcw size={14} strokeWidth={1.7} />}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {editing !== undefined && (
        <ExternalCostDialog project={project} record={editing} onClose={() => setEditing(undefined)}
          onSaved={(uploaded) => { setEditing(undefined); load(); if (uploaded) refreshDocs?.(); }} />
      )}
    </div>
  );
}

export default function VariationsTab({ project, docs, refresh, refreshDocs }) {
  const { readOnly } = useAuth();
  const [variations, setVariations] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = useCallback(() => api.get(`/projects/${project.id}/variations`).then((r) => setVariations(r.data)), [project.id]);
  useEffect(() => { load(); }, [load]);

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.code}? This cannot be undone from the interface.`)) return;
    try { await api.delete(`/variations/${v.id}`); toast.success(`${v.code} deleted`); load(); refresh?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const approvedTotal = (variations || []).filter((v) => v.status === "Approved").reduce((s, v) => s + v.amount, 0);

  return (
    <div data-testid="variations-tab" className="bdx-page-enter">
      <div className="mb-5 flex flex-col gap-4 border-b border-gray-300 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="bdx-icon-tile"><FilePenLine size={16} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">Variations Register</h2>
            <p className="mt-1 text-[11px] text-gray-500">All values Excl. GST · Only Approved variations increase the Current Approved Contract Total</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 rounded-lg border border-[#d9cde5] bg-[#f3eff7] px-4 py-2.5 shadow-sm">
            <BadgeDollarSign size={17} strokeWidth={1.8} className="text-[#604184]" />
            <div>
            <div className="bdx-label">Approved Variations</div>
            <div data-testid="variations-approved-total" className="font-mono-data font-semibold text-gray-950">{fmtAUDex(approvedTotal)}</div>
            </div>
          </div>
          {!readOnly && (
            <button data-testid="new-variation-btn" className="bdx-btn-primary" onClick={() => setEditing(null)}>
              <Plus size={15} strokeWidth={2} /> Raise Variation
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <table className="bdx-table table-fixed" style={{ minWidth: "1220px" }} data-testid="variations-table">
          <colgroup>
            <col style={{ width: "100px" }} />
            <col style={{ width: "110px" }} />
            <col />
            <col style={{ width: "170px" }} />
            <col style={{ width: "110px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "104px" }} />
            <col style={{ width: "118px" }} />
            {!readOnly && <col style={{ width: "204px" }} />}
          </colgroup>
          <thead><tr><th>No.</th><th>Date Raised</th><th>Description</th><th className="whitespace-nowrap text-right">Amount (Excl. GST)</th><th>Status</th><th>Date Approved</th><th>Claimed</th><th>Files</th>{!readOnly && <th>Actions</th>}</tr></thead>
          <tbody>
            {variations?.length === 0 && <tr><td colSpan={9} className="text-center text-gray-400 py-10">No variations raised</td></tr>}
            {variations?.map((v) => (
              <tr key={v.id} data-testid={`variation-row-${v.code}`}
                onClick={() => { if (!readOnly) setEditing(v); }}
                className={!readOnly ? "group cursor-pointer transition-colors duration-150 hover:bg-[#faf8fc]" : ""}
                title={!readOnly ? `Open ${v.code}` : undefined}>
                <td><span className="inline-flex items-center gap-1.5 rounded-md border border-[#d9cde5] bg-[#f3eff7] px-2 py-1 font-mono-data text-xs font-semibold text-[#604184]">{v.code}{v.source_type === "solar_system" && <Sun size={11} strokeWidth={2} className="text-amber-500" title="Solar Roadmap linked" />}</span></td>
                <td className="font-mono-data">{fmtDate(v.date_raised)}</td>
                <td className="truncate" title={v.description}>{v.description}</td>
                <td className="text-right font-mono-data font-semibold text-gray-950">{fmtAUDex(v.amount)}</td>
                <td><StatusBadge status={v.status} tone={TONE[v.status]} /></td>
                <td className="font-mono-data">{fmtDate(v.date_approved)}</td>
                <td>{v.claimed ? <StatusBadge status="Claimed" tone="green" /> : <StatusBadge status="Unclaimed" tone="grey" />}</td>
                <td><VariationFilesCell project={project} variation={v} docs={docs} refreshDocs={refreshDocs} readOnly={readOnly} /></td>
                {!readOnly && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button data-testid={`var-edit-${v.code}`} className="bdx-icon-btn" onClick={() => setEditing(v)} aria-label={`Edit ${v.code}`}><Pencil size={14} strokeWidth={1.7} /></button>
                      {v.source_type === "solar_system" ? (
                        <span data-testid={`var-solar-managed-${v.code}`} className="inline-flex max-w-[150px] items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-left text-[9px] font-semibold uppercase leading-[1.35] text-amber-700" style={{ letterSpacing: "0.05em" }} title="This Solar-linked variation can only be removed from the Solar Roadmap">
                          <Sun size={10} strokeWidth={2} className="shrink-0" /> <span>Managed from Solar Roadmap</span>
                        </span>
                      ) : (
                        !v.claimed && <button data-testid={`var-delete-${v.code}`} className="bdx-icon-btn-danger" onClick={() => remove(v)} aria-label={`Delete ${v.code}`}><Trash2 size={14} strokeWidth={1.7} /></button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ExternalCostsSection project={project} docs={docs} refresh={refresh} refreshDocs={refreshDocs} readOnly={readOnly} />

      {editing !== undefined && (
        <VariationForm project={project} variation={editing} docs={docs} refreshDocs={refreshDocs}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); refresh?.(); }} />
      )}
    </div>
  );
}
